<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useToaster } from '@/composables/toaster';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton/CButton.vue';
import type { VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import { DEFAULT_QR_DATA, isStyledQrConfig, updateStyledQrOptions } from '@/utils/qrCodeUtils';

const props = defineProps<{
    canvas: CanvasEx;
    vectorElement?: VectorPlaceholderElement;
    editMode?: boolean;
}>();

const emit = defineEmits<{
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
}>();

const model = reactive({
    data: DEFAULT_QR_DATA
});

const disabled = computed(() => !!props.vectorElement?.locked);

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || !isStyledQrConfig(element.qrConfig)) return;

        model.data = element.qrConfig.options.data;
    },
    { immediate: true }
);

const toaster = useToaster();
const generateQr = async () => {
    if (!model.data.trim()) return;

    try {
        emit('trigger-loading', true, 'Generating QR code...');
        await updateStyledQrOptions(props.vectorElement, props.canvas, { data: model.data }, props.editMode);
    } catch (error) {
        console.error('Error generating QR code:', error);
        toaster.error('An error occurred while generating the QR code');
    } finally {
        emit('trigger-loading', false);
    }
};
</script>

<template>
    <div class="qr-code-tester-wrapper">
        <c-input
            v-model="model.data"
            class="flex-fill"
            label="Type/Paste a URL or text to encode"
            :disabled="disabled"
        />
        <c-button
            class="ml-auto"
            variant="light"
            text="Generate QR"
            :disabled="disabled || !model.data.trim()"
            @click="generateQr"
        />
    </div>
</template>

<style scoped lang="scss">
.qr-code-tester-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
}
</style>
